// Гант «Модели ГПР» — седьмой экран нового фронта (п.20, 2026-09-07).
// Уровни: этап → работа → (корпус) → этаж. Полоску задачи можно двигать (ручное
// начало) и тянуть за правый край (ручная длительность); клик по дате — ввод
// ручной даты. Стрелки связей рисуются между полосами работ. Строки одинаковой
// высоты, поэтому список виртуализован: рисуется только видимое окно.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ask } from '../dialog';
import { call, p, type Data } from '../api';
import type { ShellCtx } from '../shell';
import { fmt, parseDate } from '../util';

type Tasks = Data<'/api/projects/{pid}/tasks', 'get'>;
type Row = Tasks['rows'][number];
type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type SvodRow = Data<'/api/link-templates/rows', 'get'>[number];

const D1 = 86400000;
const dnum = (s?: string | null) => (s ? Math.round(new Date(s + 'T00:00:00Z').getTime() / D1) : 0);
const dstr = (d: number) => new Date(d * D1).toISOString().slice(0, 10);
const MN = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const PPD: Record<string, number> = { day: 22, week: 5, month: 1.6, quarter: 0.55 };
const ROW_H = 25;
const PAD = 8;
const NO_ETAP = '— без этапа —';
const statusKind = (s?: string | null) => (s === 'СМР окончены' || s === 'СМР приняты' ? 'done'
  : s === 'СМР начаты' ? 'act' : 'plan');
const grad = (f: number, zero = '#64748b') => (f >= 1 ? 'background:#12b76a' : f <= 0 ? 'background:' + zero
  : 'background:linear-gradient(to right,#12b76a ' + Math.round(f * 100) + '%,#64748b ' + Math.round(f * 100) + '%)');
const style = (css: string) => Object.fromEntries(css.split(';').filter(Boolean).map(x => {
  const i = x.indexOf(':');
  const k = x.slice(0, i).trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
  return [k, x.slice(i + 1).trim()];
})) as React.CSSProperties;

type Group = { name: string; rows: Row[]; s: number; e: number; done: number; act: boolean };
type Line =
  | { kind: 'etap'; name: string; s: number; e: number; f: number; open: boolean }
  | { kind: 'work'; g: Group; open: boolean; single?: Row }
  | { kind: 'korpus'; key: string; name: string; s: number; e: number; f: number; open: boolean }
  | { kind: 'task'; row: Row; pad: number };

export function Gant({ ctx }: { ctx: ShellCtx }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [meta, setMeta] = useState<Record<string, Work>>({});
  const [links, setLinks] = useState<{ work: string; pred: string; tip: string }[]>([]);
  const [scale, setScale] = useState(() => { try { return localStorage.getItem('plot_gscale') || 'month'; } catch { return 'month'; } });
  const [openWork, setOpenWork] = useState<Set<string>>(new Set());
  const [openKorp, setOpenKorp] = useState<Set<string>>(new Set());
  const [closedEtap, setClosedEtap] = useState<Set<string>>(new Set());
  const [scroll, setScroll] = useState(0);
  const [height, setHeight] = useState(600);
  const [, bump] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const can = ctx.canPlan;

  const fail = (m: string) => ctx.onError?.(m);

  const load = useCallback(async () => {
    const [t, w, l] = await Promise.all([
      call<Tasks>('get', p('/api/projects/{pid}/tasks', { pid: ctx.pid })
        + '?korpus=' + encodeURIComponent(ctx.korpus || '') + '&order=name&limit=20000'),
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<SvodRow[]>('get', '/api/link-templates/rows?project=' + ctx.pid),
    ]);
    if (!t.ok) { fail(t.error.message); return; }
    if (w.ok) { const m: Record<string, Work> = {}; w.data.forEach(x => { m[x.name] = x; }); setMeta(m); }
    if (l.ok) {
      const seen = new Set<string>(); const out: { work: string; pred: string; tip: string }[] = [];
      l.data.forEach(e => { const k = e.work + '¶' + e.pred; if (seen.has(k)) return; seen.add(k); out.push({ work: e.work, pred: e.pred, tip: e.tip }); });
      setLinks(out);
    }
    ctx.onTasks?.(t.data);
    // исключённые из модели задачи на Ганте не показываются
    setRows(t.data.rows.filter(r => !r.iskl && (!ctx.matchesTask || ctx.matchesTask(r))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.korpus, ctx.reloadToken, ctx.filterKey]);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const set = () => setHeight(el.clientHeight || 600);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const groups: Group[] = useMemo(() => {
    const by = new Map<string, Row[]>();
    rows.forEach(r => {
      if (!r.nachalo || !r.okonchanie) return;
      if (!by.has(r.rabota)) by.set(r.rabota, []);
      by.get(r.rabota)!.push(r);
    });
    const gs = [...by.entries()].map(([name, rs]) => {
      rs.sort((a, b) => (parseFloat(String(a.etazh)) || 0) - (parseFloat(String(b.etazh)) || 0));
      return { name, rows: rs, s: Math.min(...rs.map(r => dnum(r.nachalo))), e: Math.max(...rs.map(r => dnum(r.okonchanie))),
        done: rs.filter(r => statusKind(r.status) === 'done').length, act: rs.some(r => statusKind(r.status) === 'act') };
    });
    gs.sort((a, b) => a.s - b.s);
    return gs;
  }, [rows]);

  // список строк: этап → работа → корпус → этаж (свёрнутое не разворачиваем)
  const lines: Line[] = useMemo(() => {
    const byEtap = new Map<string, Group[]>();
    groups.forEach(g => {
      const et = meta[g.name]?.etap || NO_ETAP;
      if (!byEtap.has(et)) byEtap.set(et, []);
      byEtap.get(et)!.push(g);
    });
    const etaps = [...byEtap.entries()].map(([et, gs]) => ({
      et, gs, s: Math.min(...gs.map(g => g.s)), e: Math.max(...gs.map(g => g.e)),
      done: gs.reduce((a, g) => a + g.done, 0), total: gs.reduce((a, g) => a + g.rows.length, 0),
    })).sort((a, b) => a.s - b.s);
    const out: Line[] = [];
    etaps.forEach(E => {
      const open = !closedEtap.has(E.et);
      out.push({ kind: 'etap', name: E.et, s: E.s, e: E.e, f: E.total ? E.done / E.total : 0, open });
      if (!open) return;
      E.gs.forEach(g => {
        if (g.rows.length === 1) { out.push({ kind: 'work', g, open: false, single: g.rows[0] }); return; }
        const isOpen = openWork.has(g.name);
        out.push({ kind: 'work', g, open: isOpen });
        if (!isOpen) return;
        const korps = [...new Set(g.rows.map(r => r.korpus).filter(Boolean))] as string[];
        if (korps.length > 1) {
          korps.forEach(k => {
            const kr = g.rows.filter(r => r.korpus === k);
            const key = g.name + '¶' + k;
            const kopen = openKorp.has(key);
            out.push({ kind: 'korpus', key, name: k, s: Math.min(...kr.map(r => dnum(r.nachalo))),
              e: Math.max(...kr.map(r => dnum(r.okonchanie))),
              f: kr.length ? kr.filter(r => statusKind(r.status) === 'done').length / kr.length : 0, open: kopen });
            if (kopen) kr.forEach(r => out.push({ kind: 'task', row: r, pad: 52 }));
          });
        } else g.rows.forEach(r => out.push({ kind: 'task', row: r, pad: 36 }));
      });
    });
    return out;
  }, [groups, meta, closedEtap, openWork, openKorp]);

  const min = groups.length ? Math.min(...groups.map(g => g.s)) : 0;
  const max = groups.length ? Math.max(...groups.map(g => g.e)) : 0;
  const ppd = PPD[scale] || 1.6;
  const W = Math.max(700, (max - min + 8) * ppd);
  const pos = (d: number) => (d - min) * ppd;

  const ticks = useMemo(() => {
    const out: { x: number; label: string }[] = [];
    if (!groups.length) return out;
    if (scale === 'day') {
      for (let d = min - 2; d <= max + 4; d++) {
        const dt = new Date(d * D1);
        out.push({ x: pos(d), label: (dt.getUTCDate() === 1 ? MN[dt.getUTCMonth()] + ' ' : '') + dt.getUTCDate() });
      }
    } else if (scale === 'week') {
      let d = min - 2;
      d -= (new Date(d * D1).getUTCDay() + 6) % 7;
      for (; d <= max + 4; d += 7) {
        const dt = new Date(d * D1);
        out.push({ x: pos(d), label: String(dt.getUTCDate()).padStart(2, '0') + '.' + String(dt.getUTCMonth() + 1).padStart(2, '0') });
      }
    } else {
      const step = scale === 'quarter' ? 3 : 1;
      let cur = new Date((min - 2) * D1);
      let m0 = cur.getUTCMonth();
      if (scale === 'quarter') m0 = Math.floor(m0 / 3) * 3;
      cur = new Date(Date.UTC(cur.getUTCFullYear(), m0, 1));
      while (cur.getTime() / D1 <= max + 4) {
        out.push({ x: pos(Math.round(cur.getTime() / D1)),
          label: scale === 'quarter' ? 'Q' + (Math.floor(cur.getUTCMonth() / 3) + 1) + ' ' + String(cur.getUTCFullYear()).slice(2)
            : MN[cur.getUTCMonth()] + ' ' + String(cur.getUTCFullYear()).slice(2) });
        cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + step, 1));
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, min, max, groups.length]);

  // перетаскивание полоски задачи: сдвиг — ручное начало, правый край — длительность
  const drag = useRef<{ row: Row; mode: 'move' | 'resize'; x0: number; el: HTMLElement; left0: number; w0: number } | null>(null);
  useEffect(() => {
    const move = (ev: MouseEvent) => {
      const d = drag.current;
      if (!d) return;
      const days = Math.round((ev.clientX - d.x0) / ppd);
      if (d.mode === 'move') d.el.style.left = (d.left0 + days * ppd) + 'px';
      else d.el.style.width = Math.max(ppd, d.w0 + days * ppd) + 'px';
    };
    const up = async (ev: MouseEvent) => {
      const d = drag.current;
      if (!d) return;
      drag.current = null;
      const days = Math.round((ev.clientX - d.x0) / ppd);
      if (!days) { bump(v => v + 1); return; }
      const r = d.row, s0 = dnum(r.nachalo), e0 = dnum(r.okonchanie);
      const body = d.mode === 'move'
        ? { rev: r.rev, nachalo_vruchnuyu: dstr(s0 + days) }
        : { rev: r.rev, dlit_ruchnaya: Math.max(1, e0 - s0 + days) };
      const res = await call<{ rev: number }>('patch', p('/api/tasks/{tid}', { tid: r.id }), body);
      if (!res.ok) {
        fail(res.error.message + (res.error.code === 'stale' ? ' Обновите Гант (кнопка «Пересчитать»).' : ''));
        void load();
        return;
      }
      r.rev = res.data.rev;
      if (d.mode === 'move') {
        r.nachalo = dstr(s0 + days); r.okonchanie = dstr(e0 + days); r.nach_vr = r.nachalo; r.ruchnoe = true;
        ctx.onSaved?.('Ручное начало «' + r.rabota + '» эт.' + r.etazh + ': ' + fmt(r.nachalo)
          + '. Нажмите «Пересчитать», чтобы пересчитать зависимые.');
      } else {
        const dl = Math.max(1, e0 - s0 + days);
        r.dlit_r = dl; r.dlit = dl; r.okonchanie = dstr(s0 + dl);
        ctx.onSaved?.('Ручная длительность «' + r.rabota + '» эт.' + r.etazh + ': ' + dl
          + ' дн. Нажмите «Пересчитать», чтобы пересчитать зависимые.');
      }
      setRows(rs => rs.slice());
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  });

  async function editDate(r: Row, isStart: boolean) {
    const v = await ask((isStart ? 'Ручное начало' : 'Ручное окончание') + ' (ДД.ММ.ГГГГ; пусто или «-» — очистить):',
      fmt(isStart ? (r.nach_vr || r.nachalo) : (r.okonch_vr || r.okonchanie)));
    if (v === null) return;
    const t = v.trim();
    let iso: string | null = null;
    if (t && t !== '-') {
      const parsed = parseDate(t);
      if (parsed === undefined) { fail('Дата не разобрана: ' + t); return; }
      iso = parsed;
    }
    const res = await call<{ rev: number }>('patch', p('/api/tasks/{tid}', { tid: r.id }),
      { rev: r.rev, [isStart ? 'nachalo_vruchnuyu' : 'okonchanie_vruchnuyu']: iso });
    if (!res.ok) { fail(res.error.message); return; }
    r.rev = res.data.rev;
    if (isStart) { r.nach_vr = iso; if (iso) r.nachalo = iso; }
    else { r.okonch_vr = iso; if (iso) r.okonchanie = iso; }
    r.ruchnoe = !!(r.nach_vr || r.okonch_vr || r.vn || r.vo);
    ctx.onSaved?.((isStart ? 'Ручное начало: ' : 'Ручное окончание: ') + (iso ? fmt(iso) : 'очищено')
      + ' — нажмите «Пересчитать», чтобы пересчитать зависимые.');
    setRows(rs => rs.slice());
  }

  // стрелки связей: между полосами работ, координаты берём из раскладки строк
  const barY = useMemo(() => {
    const m = new Map<string, { y: number; s: number; e: number }>();
    lines.forEach((l, i) => {
      if (l.kind !== 'work') return;
      const r = l.single;
      m.set(l.g.name, { y: i * ROW_H + 12, s: r ? dnum(r.nachalo) : l.g.s, e: r ? dnum(r.okonchanie) : l.g.e });
    });
    return m;
  }, [lines]);

  const first = Math.max(0, Math.floor(scroll / ROW_H) - PAD);
  const last = Math.min(lines.length, Math.ceil((scroll + height) / ROW_H) + PAD);

  if (!groups.length) {
    return (
      <div>
        <ScaleBar scale={scale} onScale={s => { setScale(s); try { localStorage.setItem('plot_gscale', s); } catch { /* приватный режим */ } }} />
        <div className="tbl" style={{ padding: 16 }}>Нет данных с датами.</div>
      </div>
    );
  }
  return (
    <div>
      <ScaleBar scale={scale} onScale={s => { setScale(s); try { localStorage.setItem('plot_gscale', s); } catch { /* приватный режим */ } }} />
      <div className="tbl" ref={box} style={{ maxHeight: '76vh', position: 'relative' }}
        onScroll={e => setScroll((e.target as HTMLDivElement).scrollTop)}>
        <div className="g-head">
          <div className="g-name">Работа</div>
          <div className="g-d g-d1">Начало</div><div className="g-d g-d2">Оконч.</div>
          <div className="g-d g-d3" title="Длительность: у задачи — её длительность, у группы — окончание − начало">Длит.</div>
          <div className="g-track" style={{ minWidth: W }}>
            {ticks.map((t, i) => <div key={i} className="g-month" style={{ left: t.x }}>{t.label}</div>)}
          </div>
        </div>
        <div style={{ position: 'relative', height: lines.length * ROW_H }}>
          <svg className="g-links" width={W + 460} height={lines.length * ROW_H}
            style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', zIndex: 0 }}>
            <defs><marker id="garr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#475467" /></marker></defs>
            {links.map((l, i) => {
              const a = barY.get(l.pred), b = barY.get(l.work);
              if (!a || !b) return null;
              const x1 = 460 + pos(l.tip === 'НН' ? a.s : a.e + 1);
              const x2 = 460 + pos(b.s) - 2;
              return <path key={i} d={'M ' + x1 + ' ' + a.y + ' C ' + (x1 + 18) + ' ' + a.y + ', ' + (x2 - 18) + ' ' + b.y + ', ' + x2 + ' ' + b.y}
                stroke={l.tip === 'НН' ? '#7c3aed' : '#475467'} strokeWidth={1.4} fill="none" opacity={0.65} markerEnd="url(#garr)" />;
            })}
          </svg>
          {lines.slice(first, last).map((l, i) => {
            const top = (first + i) * ROW_H;
            const common: React.CSSProperties = { position: 'absolute', top, left: 0, right: 0, height: ROW_H, boxSizing: 'border-box' };
            if (l.kind === 'etap') {
              return (
                <div key={'e' + l.name} className="g-row" style={{ ...common, background: '#e7ecf4' }}>
                  <div className="g-name" style={{ fontWeight: 700, background: '#e7ecf4' }}
                    onClick={() => setClosedEtap(s => { const n = new Set(s); if (n.has(l.name)) n.delete(l.name); else n.add(l.name); return n; })}>
                    {(l.open ? '▾ ' : '▸ ') + l.name}</div>
                  <div className="g-d g-d1" style={{ background: '#e7ecf4', fontWeight: 600 }}>{fmt(dstr(l.s))}</div>
                  <div className="g-d g-d2" style={{ background: '#e7ecf4', fontWeight: 600 }}>{fmt(dstr(l.e))}</div>
                  <div className="g-d g-d3" style={{ background: '#e7ecf4', fontWeight: 600 }}>{l.e - l.s}</div>
                  <div className="g-track" style={{ minWidth: W }}>
                    <div className="g-bar" title={l.name + ': ' + fmt(dstr(l.s)) + ' — ' + fmt(dstr(l.e))}
                      style={{ left: pos(l.s), width: Math.max(2, (l.e - l.s + 1) * ppd), ...style(grad(l.f)) }} /></div>
                </div>);
            }
            if (l.kind === 'korpus') {
              return (
                <div key={'k' + l.key} className="g-row" style={common}>
                  <div className="g-name" style={{ paddingLeft: 34, fontWeight: 600 }}
                    onClick={() => setOpenKorp(s => { const n = new Set(s); if (n.has(l.key)) n.delete(l.key); else n.add(l.key); return n; })}>
                    {(l.open ? '▾ ' : '▸ ') + l.name}</div>
                  <div className="g-d g-d1">{fmt(dstr(l.s))}</div><div className="g-d g-d2">{fmt(dstr(l.e))}</div>
                  <div className="g-d g-d3">{l.e - l.s}</div>
                  <div className="g-track" style={{ minWidth: W }}>
                    <div className="g-bar" title={l.name + ': ' + fmt(dstr(l.s)) + ' — ' + fmt(dstr(l.e))}
                      style={{ left: pos(l.s), width: Math.max(2, (l.e - l.s + 1) * ppd), ...style(grad(l.f)) }} /></div>
                </div>);
            }
            if (l.kind === 'work') {
              const g = l.g;
              if (l.single) {
                const r = l.single, s0 = dnum(r.nachalo), e0 = dnum(r.okonchanie);
                return (
                  <div key={'w' + g.name} className="g-row" style={common}>
                    <div className="g-name" style={{ paddingLeft: 18, cursor: 'default' }} title={g.name}>
                      {g.name}{r.etazh ? <span className="muted"> эт. {r.etazh}</span> : null}
                      {r.status ? <span className="muted"> {r.status}</span> : null}</div>
                    <div className={'g-d g-d1 g-de' + (r.nach_vr ? ' manual-d' : '')} title="клик — ручное начало"
                      onClick={() => { if (can) void editDate(r, true); }}>{fmt(r.nachalo)}</div>
                    <div className={'g-d g-d2 g-de' + (r.okonch_vr ? ' manual-d' : '')} title="клик — ручное окончание"
                      onClick={() => { if (can) void editDate(r, false); }}>{fmt(r.okonchanie)}</div>
                    <div className="g-d g-d3">{r.dlit ?? ''}</div>
                    <div className="g-track" style={{ minWidth: W }}>
                      <Bar r={r} left={pos(s0)} width={Math.max(2, (e0 - s0 + 1) * ppd)} can={can} ppd={ppd} drag={drag}
                        title={g.name + ': ' + fmt(r.nachalo) + ' — ' + fmt(r.okonchanie)} /></div>
                  </div>);
              }
              const f = g.rows.length ? g.done / g.rows.length : 0;
              return (
                <div key={'w' + g.name} className="g-row" style={common}>
                  <div className="g-name" style={{ paddingLeft: 18 }} title={g.name}
                    onClick={() => setOpenWork(s => { const n = new Set(s); if (n.has(g.name)) n.delete(g.name); else n.add(g.name); return n; })}>
                    {(l.open ? '▾ ' : '▸ ') + g.name} <span className="muted">({g.done}/{g.rows.length})</span></div>
                  <div className="g-d g-d1">{fmt(dstr(g.s))}</div><div className="g-d g-d2">{fmt(dstr(g.e))}</div>
                  <div className="g-d g-d3">{g.e - g.s}</div>
                  <div className="g-track" style={{ minWidth: W }}>
                    <div className="g-bar" title={g.name + ': ' + fmt(dstr(g.s)) + ' — ' + fmt(dstr(g.e))}
                      style={{ left: pos(g.s), width: Math.max(2, (g.e - g.s + 1) * ppd), ...style(grad(f, g.act ? '#f79009' : '#64748b')) }} /></div>
                </div>);
            }
            const r = l.row, s0 = dnum(r.nachalo), e0 = dnum(r.okonchanie);
            return (
              <div key={'t' + r.id} className="g-row" style={common}>
                <div className="g-name flr" style={{ paddingLeft: l.pad }}>эт. {r.etazh}
                  {r.status ? <span className="muted"> {r.status}</span> : null}</div>
                <div className={'g-d g-d1 g-de' + (r.nach_vr ? ' manual-d' : '')} title="клик — ручное начало"
                  onClick={() => { if (can) void editDate(r, true); }}>{fmt(r.nachalo)}</div>
                <div className={'g-d g-d2 g-de' + (r.okonch_vr ? ' manual-d' : '')} title="клик — ручное окончание"
                  onClick={() => { if (can) void editDate(r, false); }}>{fmt(r.okonchanie)}</div>
                <div className="g-d g-d3">{r.dlit ?? ''}</div>
                <div className="g-track" style={{ minWidth: W }}>
                  <Bar r={r} left={pos(s0)} width={Math.max(2, (e0 - s0 + 1) * ppd)} can={can} ppd={ppd} drag={drag}
                    title={'эт.' + r.etazh + ': ' + fmt(r.nachalo) + ' — ' + fmt(r.okonchanie)} /></div>
              </div>);
          })}
        </div>
      </div>
    </div>
  );
}

function ScaleBar({ scale, onScale }: { scale: string; onScale: (s: string) => void }) {
  return (
    <div className="bar">
      <label>Масштаб <select value={scale} onChange={e => onScale(e.target.value)}>
        <option value="day">День</option><option value="week">Неделя</option>
        <option value="month">Месяц</option><option value="quarter">Квартал</option>
      </select></label>
      <span className="pw-muted">Клик по работе — развернуть этажи. Зелёное — выполнено, жёлтое — в работе, синее — план.
        Полоску задачи можно перетащить (ручное начало) или потянуть за правый край (ручная длительность).</span>
    </div>
  );
}

/** Полоска задачи: тянется мышью, правый край меняет длительность */
function Bar({ r, left, width, title, can, ppd, drag }: {
  r: Row; left: number; width: number; title: string; can: boolean; ppd: number;
  drag: React.MutableRefObject<{ row: Row; mode: 'move' | 'resize'; x0: number; el: HTMLElement; left0: number; w0: number } | null>;
}) {
  return (
    <div className={'g-bar g-ed c-' + statusKind(r.status)} title={title + (can
      ? '. Перетащите полоску — ручное начало; потяните правый край — ручная длительность' : '')}
      style={{ left, width, cursor: can ? 'grab' : 'default' }}
      onMouseMove={e => {
        if (!can) return;
        const el = e.currentTarget;
        el.style.cursor = el.getBoundingClientRect().right - e.clientX <= 6 ? 'ew-resize' : 'grab';
      }}
      onMouseDown={e => {
        if (!can) return;
        const el = e.currentTarget;
        const rect = el.getBoundingClientRect();
        drag.current = { row: r, mode: rect.right - e.clientX <= 6 ? 'resize' : 'move', x0: e.clientX, el, left0: left, w0: width };
        void ppd;
        e.preventDefault();
      }} />
  );
}
